// client/src/store/products_config/components/ProductDraftForm.jsx
import { useEffect, useRef } from "react";
import { useForm, FormProvider, useWatch, useFormContext } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import schema from "../validationSchema";
import CategorySelect from "./CategorySelect";
import PhotoPicker from "./PhotoPicker";
import { useCurrencyMeta } from "../../store_settings/hooks/useCurrencyMeta";
import { useId } from "react";

const INITIAL = {
  name: "",
  description: "",
  category: [],
  type: "physical",
  price: 0,
  // stock/unlimited/SKU removed
  width: "",
  height: "",
  depth: "",
  weight: "",
  photos: [],
  ageRestricted: false,
  video: null, // { id?, video_url?, file? } or null
};

export default function ProductDraftForm({ product, onChange, businessId , baseCurrency = "USD" }) {
  const methods = useForm({
    resolver: yupResolver(schema, { abortEarly: false }),
    mode: "onChange",
    defaultValues: { ...INITIAL, ...product },
  });

  const { control, reset, formState: { errors } } = methods;

  // hydrate only when switching to a different draft (or on mount)
  useEffect(() => {
    reset({ ...INITIAL, ...product });
    // immediately surface all validation errors on mount/switch
    // (no focus jump)
    Promise.resolve().then(() => methods.trigger(undefined, { shouldFocus: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // bubble changes to parent draft, but only when the payload actually changes
  const all = useWatch({ control });
  const lastSent = useRef("");
  useEffect(() => {
    const payload = all ?? {};
    const sig = JSON.stringify(payload); // cheap stability key
    if (sig !== lastSent.current) {
      lastSent.current = sig;
      onChange(payload);
    }
  }, [all, onChange]);

  const type = all?.type || "physical";

  // Local handlers for media caps
  const setPhotosCapped = (arr) => {
    const capped = (arr || []).slice(0, 5); // hard-cap at 5
    methods.setValue("photos", capped, { shouldDirty: true, shouldValidate: true });
  };
  const setVideo = (val) => {
    methods.setValue("video", val, { shouldDirty: true, shouldValidate: false });
  };
    
  useEffect(() => {
    if (all?.ageRestricted) {
      methods.trigger("ageRestrictedMin", { shouldFocus: false });
    } else {
      methods.clearErrors("ageRestrictedMin");
    }
  }, [all?.ageRestricted, methods]);


  return (
    <FormProvider {...methods}>
      <form className="space-y-6">
        {/* Basic */}
        <div className="grid md:grid-cols-2 gap-4">
          <Input name="name" label="Product Name *" required />
          {/* SKU removed */}
          <div className="md:col-span-2">
            <Label>Description</Label>
            <textarea
              className={`input min-h-[90px] rounded-xl border border-gray-300 bg-white
                          focus:border-gray-400 focus:ring-2 focus:ring-indigo-200
                          ${errors?.description ? "border-red-500 focus:ring-red-200" : ""}`}
              rows="3"
              {...methods.register("description")}
              placeholder="Describe your product…"
            />
            <FieldErr err={errors?.description} />
          </div>
        </div>

        {/* Category & Type */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <CategorySelect
              value={all.category}
              onChange={(val) => methods.setValue("category", val, { shouldDirty: true, shouldValidate: true })}
              error={errors?.category}
            />
            {/* Keep a second line as well for consistency */}
            <FieldErr err={errors?.category} />
          </div>
          <Select
            name="type"
            label="Product Type"
            options={[["physical", "Physical"], ["digital", "Digital"], ["service", "Service"]]}
          />
        </div>

        {/* Pricing */}
        <div className="grid md:grid-cols-3 gap-4">
          <Input
            name="price"
            type="number"
            min="0"
            label="Price *"
            currency={baseCurrency}
            money // <-- only price is money-aware
          />
        </div>
        <FieldErr err={errors?.price} />
        {/* Age restriction */}
        <div className="rounded-xl border border-gray-300 bg-white p-3 space-y-2">
          <Label>Age restriction</Label>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="ageRestricted"
              {...methods.register("ageRestricted")}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="ageRestricted" className="text-sm text-gray-800">
              This product is age restricted
            </label>
          </div>

          {all?.ageRestricted && (
            <div className="pt-2">
              <Input
                name="ageRestrictedMin"
                type="number"
                min="1"
                max="25"
                step="1"
                label="Minimum age (years) *"
                className="w-28"
              />
              {/* Input renders its own FieldErr via <Input/> */}
            </div>
          )}
        </div>

        {/* Stock removed entirely */}

        {/* Physical-only */}
        {type === "physical" && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Input name="weight" type="number" step="0.01" label="Weight (kg)" className="w-20" />
              <Input name="width"  type="number" step="0.01" label="Width (cm)"  className="w-28" />
              <Input name="height" type="number" step="0.01" label="Height (cm)" className="w-28" />
              <Input name="depth"  type="number" step="0.01" label="Depth (cm)"  className="w-28" />
            </div>
            <FieldErr err={errors?.shippingGroup} />
          </>
        )}

        {/* Media */}
        <div className="rounded-xl border border-gray-300 bg-white p-3 space-y-3">
          <div>
            <Label>Photos</Label>
            <PhotoPicker
              value={(all.photos || []).slice(0, 5)}
              onChange={setPhotosCapped}
            />
            {/* Show array-level error (and summary if nested issues) */}
            <FieldErr err={errors?.photos} />
            <p className="text-[11px] text-gray-500 mt-1">Up to 5 photos.</p>
          </div>

          <div>
            <Label>Video (optional)</Label>
            <VideoInput value={all.video} onChange={setVideo} />
            <FieldErr err={errors?.video} />
            <p className="text-[11px] text-gray-500 mt-1">Add one video file (MP4, WebM, MOV…)</p>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

// ----- Inputs used by the form -----
function Input({ name, label, className = "", money = false, currency = "USD", ...props }) {
  const { register, formState: { errors } } = useFormContext();

  // Call the hook unconditionally (OK per hooks rules)
  const meta = useCurrencyMeta(currency);
  const digits = meta?.digits ?? 2;
  const stepFromMoney = digits > 0 ? 1 / Math.pow(10, digits) : 1;

  // Only apply the derived step when money=true
  const step = money ? stepFromMoney : props.step;

  return (
    <div>
      <Label>{label}</Label>
      <input
        {...register(name)}
        {...props}
        step={step}
        aria-invalid={!!errors?.[name]}
        className={`input rounded-xl border border-gray-300 bg-white
                    focus:border-gray-400 focus:ring-2 focus:ring-indigo-200
                    ${errors?.[name] ? "border-red-500 focus:ring-red-200" : ""} ${className}`}
      />
      <FieldErr err={errors?.[name]} />
      {money && (
        <p className="mt-1 text-[11px] text-gray-500">
          Currency: {currency} · {digits} decimal{digits === 1 ? "" : "s"}
        </p>
      )}
    </div>
  );
}

function Select({ name, label, options }) {
  const { register, formState: { errors } } = useFormContext();
  return (
    <div>
      <Label>{label}</Label>
      <select
        className={`input rounded-xl border border-gray-300 bg-white
                    focus:border-gray-400 focus:ring-2 focus:ring-indigo-200
                    ${errors?.[name] ? "border-red-500 focus:ring-red-200" : ""}`}
        aria-invalid={!!errors?.[name]}
        {...register(name)}
      >
        {options.map(([v, t]) => (
          <option key={v} value={v}>{t}</option>
        ))}
      </select>
      <FieldErr err={errors?.[name]} />
    </div>
  );
}

function Label({ children }) {
  return <label className="text-sm font-medium text-gray-800 mb-1 block">{children}</label>;
}

// Recursively pick the first useful validation message (arrays/objects included)
function FieldErr({ err }) {
  if (!err) return null;

  const firstMsg = (e) => {
    if (!e) return null;
    if (typeof e === "string") return e;
    if (e.message) return e.message;
    if (Array.isArray(e)) {
      for (const item of e) {
        const m = firstMsg(item);
        if (m) return m;
      }
    } else if (typeof e === "object") {
      for (const v of Object.values(e)) {
        const m = firstMsg(v);
        if (m) return m;
      }
    }
    return null;
  };

  const msg = firstMsg(err) || "Please check this field.";
  return <p className="text-xs text-red-600 mt-1">{msg}</p>;
}

/* Simple single-video input with tiny preview */
function VideoInput({ value, onChange }) {
  const uid = useId();
  const inputId = `product-video-input-${uid}`;
  const handleFile = (file) => {
    if (!file) {
      onChange(null);
      return;
    }
    onChange({ file, video_url: null });
  };

  const clear = () => onChange(null);

  const src = value?.video_url || (value?.file ? URL.createObjectURL(value.file) : "");

  return (
    <div
      className="border-dashed border-2 rounded-2xl p-4 text-center text-gray-600 hover:bg-gray-50 transition"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f && f.type.startsWith("video/")) handleFile(f);
      }}
    >
      <input
        type="file"
        accept="video/*"
        onChange={(e) => handleFile(e.target.files?.[0] || null)}
        className="hidden"
        id={inputId}
      />
      {!src ? (
        <label htmlFor={inputId} className="cursor-pointer font-medium">
          Drag & drop or{" "}
          <span className="text-indigo-600 underline underline-offset-4 decoration-indigo-300">browse</span>
        </label>
      ) : (
        <div className="space-y-2">
          <video src={src} controls className="w-full max-h-60 rounded-lg bg-black" />
          <div className="flex justify-center gap-2">
            <label htmlFor={inputId} className="btn-secondary cursor-pointer rounded-xl">
              Replace
            </label>
            <button type="button" className="btn-danger rounded-xl" onClick={clear}>
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
